/**
 * A custom hook used to initialize or reinitialize a BM page after a configuration has been loaded.
 */
import { useEffect } from 'react'
import { getAndDeleteEysStartConfigId } from './sessionStorageUtil'
import {
  buildRelativeLandingPageUrl,
  buildRelativePublicConfigLink,
} from './UrlBuilders'
import { getUrlParametersToString } from './getUrlParametersToString'
import {
  getSessionInitDataState,
  getStartupData,
  setGuidedOfferingState,
  setGuidedViewPage,
  setMarketDenomination,
  setUnavailableImageFrames,
} from '../store/sessionDataSlice'
import { SESSION_FAILURE } from '../api/errors'
import {
  getTruckInfoState,
  setLatestConfigChangeResult,
} from '../store/truckInfoSlice'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { useClient } from './useClient'
import { getMarketLanguageState } from '../store/appSlice'
import { ConfigChangeResult } from '../api/generated'
import { ImageSeriesFrame } from '../store/types'

export function usePageUpdate(
  handleFatalError: () => void,
  handleSessionFailure: () => void,
) {
  const dispatch = useAppDispatch()
  const client = useClient()
  const startupData = useAppSelector(getStartupData)
  const sessionInitData = useAppSelector(getSessionInitDataState)
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const truckInfo = useAppSelector(getTruckInfoState)

  // Initialize or reinitialize after a configuration has been loaded.
  useEffect(() => {
    const sessionId = sessionInitData?.sessionId
    if (!startupData) {
      return // Not initialized yet.
    }
    if (!client) {
      return // Not initialized yet.
    }
    if (!sessionId) {
      return // Not initialized yet.
    }
    if (!marketLanguage) {
      return // Not initialized yet.
    }
    if (!truckInfo) {
      const eysStartConfig = getAndDeleteEysStartConfigId()
      let reloadUrl
      if (eysStartConfig) {
        console.log('Explore Your Scania mode, reloading start configuration.')
        const relativeUrl = buildRelativePublicConfigLink(
          marketLanguage,
          eysStartConfig,
        )
        const params = relativeUrl.params
        params.append('eys', '1')
        reloadUrl = relativeUrl.path + getUrlParametersToString(params)
      } else {
        // This is the expected result if the user reloads the browser tab while
        // viewing the GuidedViewPage, or if a user navigates to this URL without a
        // initialized session with a loaded configuration.
        //
        // TODO: Consider using sessionStorage to store the sessionId to allow
        // refreshing the browser tab and resuming the previous session.
        //
        console.warn(
          'Expected the session to have a loaded configuration, reloading the landing page.',
        )
        const relativeUrl = buildRelativeLandingPageUrl(marketLanguage)
        const params = relativeUrl.params
        reloadUrl = relativeUrl.path + getUrlParametersToString(params)
      }
      document.location.href = reloadUrl
      dispatch(setGuidedViewPage(null))

      // IMPORTANT: The browser may execute code while the new URL is loading.
      return
    }

    dispatch(setGuidedOfferingState(null))

    const asyncWrapper = async () => {
      // Assume the session is in build mode and has a loaded configuration.
      // TODO: Store the expected session stage, GO or Build Mode in Redux or in
      // App.tsx.
      const latestConfigChangeResponse =
        await client.getLatestConfigChangeResult(sessionId)

      if (latestConfigChangeResponse.error === SESSION_FAILURE) {
        handleSessionFailure()
        return
      }
      if (!latestConfigChangeResponse.success) {
        console.error('Expected .success to be defined.')
        handleFatalError()
        return
      }
      const rebopResult = latestConfigChangeResponse.success
      dispatch(setLatestConfigChangeResult(rebopResult))
      const newMarketDenomination =
        rebopResult.auxCarrier.auxiliaries['MarketDenomination'].value
      dispatch(setMarketDenomination(newMarketDenomination || null))
      const unavailable = getUnavailableFrames(rebopResult)
      dispatch(setUnavailableImageFrames(unavailable))
    }
    asyncWrapper()
  }, [
    client,
    dispatch,
    handleFatalError,
    handleSessionFailure,
    marketLanguage,
    sessionInitData,
    startupData,
    truckInfo,
  ])
}
function getUnavailableFrames(
  rebopResult: ConfigChangeResult,
): ImageSeriesFrame[] {
  return (
    rebopResult.auxCarrier.auxiliaries['FrameFilter'].ignoredFrames
      ?.map<ImageSeriesFrame>((o) => ({
        seriesId: o.series,
        frameId: o.frame,
      }))
      .sort((a, b) => {
        const cmp = a.seriesId.localeCompare(b.seriesId)
        if (cmp !== 0) {
          return cmp
        }
        return a.frameId - b.frameId
      }) || []
  )
}
